export type FunnelLivenessStatus =
  | 'ok'
  | 'idle'
  | 'warning'
  | 'down'
  | 'unknown'
  | 'unscheduled'
  | 'running';

export interface StageLiveness {
  kind: 'runnable' | 'derived';
  status: FunnelLivenessStatus | null;
  scheduled?: boolean | null;
  age_minutes?: number | null;
  expected_interval_min?: number | null;
  /** ISO timestamp of the active run when status is 'running'; null otherwise. */
  run_started_at?: string | null;
  /** Shared label from server/lib/funnel-liveness.ts; null for derived stages. */
  canonical_label?: string | null;
}

interface FunnelStageLivenessProps {
  liveness?: StageLiveness;
}

const STATUS_DOT_CLASSES: Record<FunnelLivenessStatus, string> = {
  ok: 'bg-green-500',
  idle: 'bg-gray-400',
  warning: 'bg-yellow-500',
  down: 'bg-red-500',
  unknown: 'bg-gray-400',
  unscheduled: 'bg-slate-300',
  // CHO-728: running is a distinct blue, with a static ring so the state reads
  // even with no motion at all (the pulse is motion-safe gated).
  running: 'bg-blue-500',
};

// Static ring for running: present regardless of prefers-reduced-motion.
const RUNNING_RING_CLASS = 'ring-2 ring-blue-400/40';
// Low-key, opacity-only pulse (~2 s cycle), gated behind motion-safe.
const RUNNING_PULSE_CLASS = 'motion-safe:animate-pulse';

function formatAge(ageMinutes: number | null | undefined): string {
  if (ageMinutes == null) return '—';
  return `${ageMinutes}m ago`;
}

function formatRunAge(runStartedAt: string | null | undefined): string | null {
  if (!runStartedAt) return null;
  const ms = Date.now() - new Date(runStartedAt).getTime();
  if (Number.isNaN(ms)) return null;
  return `${Math.max(0, Math.round(ms / 60000))}m ago`;
}

function buildAriaLabel(liveness: StageLiveness): string {
  if (liveness.kind === 'derived') {
    return 'Stage state: derived, computed from an earlier stage';
  }
  if (liveness.status === 'running') {
    const started = formatRunAge(liveness.run_started_at) ?? 'just now';
    const interval = liveness.expected_interval_min ?? '—';
    return `Stage state: running, started ${started}, expected every ${interval} minutes`;
  }
  if (liveness.scheduled === false) {
    return 'Stage state: not scheduled, no job is scheduled for this stage';
  }
  const status = liveness.status ?? 'unknown';
  const age = formatAge(liveness.age_minutes);
  const interval = liveness.expected_interval_min ?? '—';
  const stale = status === 'warning' || status === 'down' ? ', stale' : '';
  return `Stage state: ${status}${stale}, last heartbeat ${age}, expected every ${interval} minutes`;
}

export default function FunnelStageLiveness({ liveness }: FunnelStageLivenessProps) {
  if (!liveness) return null;

  if (liveness.kind === 'derived') {
    return (
      <span
        className="inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
        title="computed from an earlier stage — cannot go stale"
        aria-label={buildAriaLabel(liveness)}
      >
        derived
      </span>
    );
  }

  const running = liveness.status === 'running';

  if (liveness.scheduled === false && !running) {
    return (
      <span
        className="inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded font-medium bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
        title="no job is scheduled for this stage"
        aria-label={buildAriaLabel(liveness)}
      >
        not scheduled
      </span>
    );
  }

  const status = liveness.status ?? 'unknown';
  const dotClass = STATUS_DOT_CLASSES[status] ?? 'bg-gray-400';
  const staleMarker = status === 'warning' || status === 'down' ? ' · stale' : '';
  const interval = liveness.expected_interval_min ?? '—';
  const startedAgo = running ? formatRunAge(liveness.run_started_at) : null;

  return (
    <span
      className="inline-flex items-center gap-1.5 mt-0.5 text-[10px] text-gray-600 dark:text-gray-400"
      aria-label={buildAriaLabel(liveness)}
    >
      <span
        className={[
          'inline-block w-1.5 h-1.5 rounded-full',
          dotClass,
          running ? RUNNING_RING_CLASS : '',
          running ? RUNNING_PULSE_CLASS : '',
        ].filter(Boolean).join(' ')}
      />
      <span>
        {running
          ? `running · started ${startedAgo ?? 'just now'} · every ${interval}m`
          : `${formatAge(liveness.age_minutes)} · every ${interval}m${staleMarker}`}
      </span>
    </span>
  );
}
